import { computed, ref, watch } from 'vue';

import { useRoute, useRouter } from 'vue-router';

import { defineStore } from 'pinia';

import { ElNotification } from 'element-plus';

import { $axios, $axiosManager } from '@/api/$axios.js';
import { currentPath, mirrorsMarketplaceInUrl } from '@/api/interceptors/channelAccountHeader.js';
import { profileCanAccessFeature } from '@/config/marketplaceCapabilities.js';
import { hasEditAccess } from '@/helpers/amazonAdsAccess.js';
import { consumeSignupAttribution } from '@/helpers/attribution.js';
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
  getItemFromSessionStorage,
  setItemToSessionStorage,
} from '@/helpers/localStorageServices.js';
import { markNewBusinessProfile } from '@/helpers/newBusinessProfiles.js';
import {
  isSellerProfile,
  isVendorProfile,
  isBookProfile,
  isSpEligibleProfile,
  marketplaceCategory,
  normalizeAccountType,
} from '@/helpers/marketplaceAccountType.js';
import {
  pickAutoDefaultMarketplace,
  selectableAdsCandidates,
} from '@/helpers/marketplaceAutoDefault.js';
import {
  buildSellerOnlyMarketplaces,
  findDefaultMarketplace,
  findMarketplaceByUid,
  getMarketplaceUid,
} from '@/helpers/marketplaceUnion.js';
import i18n from '@/plugins/i18n';
import { adsClaimBusinessId } from '@/router/adsClaimContext';
import { spClaimBusinessId } from '@/router/spClaimContext';
import { getHomeRoute } from '@/router/utils.js';
import { upsertUserSetting, getUserSettingsByPage } from '@/services/api/userSettingsApi';
import { bpSelection } from '@/services/auth/bpSelection';
import businessProfileMockService from '@/services/mock/businessProfileMockService.js';
import { useAlertsStore } from '@/stores/alerts.js';
import { useAuthStore } from '@/stores/auth.js';
import { useLoaderStore } from '@/stores/loader.js';
import { trackEvent } from '@/utils/ga';

export const useAccountStore = defineStore('account', () => {
  const route = useRoute();
  const router = useRouter();
  const { setErrors } = useAlertsStore();
  const loaderStore = useLoaderStore();
  const authStore = useAuthStore();

  const businessProfiles = ref([]);
  const currentBusinessProfile = ref(null);
  const currentMarketplace = ref(null);
  const withoutBusinessProfile = ref(null);
  // Profile just created whose adoption into the session token failed; null otherwise.
  const unadoptedBusinessProfile = ref(null);
  const withoutMarketplace = ref(null);

  // Keep the synchronous BP-intent holder (read by token/BP resolution) in lockstep with the
  // selected profile, covering EVERY assignment path (setter + direct writes + multi-tab +
  // logout). flush:'sync' so it lands in the same tick as the assignment — no microtask lag for
  // in-flight requests. NOT immediate: the bootstrap seed (URL/storage) must survive until a
  // profile is actually selected; changeBusinessProfile also sets it eagerly before the store
  // object updates, to cover the switch window.
  watch(
    () => currentBusinessProfile.value?.id,
    (id) => bpSelection.set(id ?? null),
    { flush: 'sync' },
  );

  // Centralized loading state
  const isLoadingProfiles = ref(false);
  const lastProfileSync = ref(null);
  const profileSyncStatus = ref('idle'); // 'idle' | 'syncing' | 'synced' | 'error'
  let syncPollingInterval = null;

  // Map to track pending promises to avoid duplicate requests
  const pendingRequests = new Map();
  // Spend/campaign ranks, keyed by business profile id. Cleared on logout.
  const autoDefaultCache = new Map();
  // Server-confirmed defaults only; localStorage fallbacks must still be saved.
  const confirmedDefaultMarketplaceUids = new Map();
  const AUTO_DEFAULT_BUDGET_MS = 4000;

  // BroadcastChannel for multi-tab synchronization (guarded — not available in every environment)
  const profileSyncChannel = 'BroadcastChannel' in window ? new BroadcastChannel('profile-sync') : null;
  let isSyncingFromThisTab = false;

  // Listen for updates from other tabs
  if (profileSyncChannel) profileSyncChannel.onmessage = async (event) => {
    if (event.data.type === 'profiles-updated' && !isSyncingFromThisTab) {
      try {
        const profiles = await fetchAllBusinessProfiles();
        businessProfiles.value = profiles;

        // updateCurrentProfileAndMarketplace will cleanly try to keep the currently selected
        // profile and marketplace active without reloading the URL if it still exists.
        updateCurrentProfileAndMarketplace(profiles, {
          updateUrl: false,
          persistToStorage: true,
        });

        // If we lost our current profile due to a temporary 401 error that cleared state,
        // syncBusinessProfiles has logic to restore it.
        if (!currentBusinessProfile.value) {
          // Re-trigger the main sync logic quietly to restore from storage/url if needed
          // It uses the cached businessProfiles.value, so it won't hit the API again
          syncBusinessProfiles();
        }
      } catch (error) {
        console.error('[Multi-tab] Failed to sync from other tab:', error);

        // Handle authentication errors
        if (error.response?.status === 401 || error.message?.includes('401')) {
          console.warn('[Multi-tab] Authentication error detected - ignore here, let interceptor handle it');
          // DO NOT clear local data here, as it causes race conditions and redirects
          // when other tabs are still in the process of refreshing the token!
        }
      }
    }
  };

  // Simple event emitter for marketplace changes
  const marketplaceEmitter = {
    events: {},
    on(event, callback) {
      if (!this.events[event]) this.events[event] = [];
      this.events[event].push(callback);
    },
    off(event, callback) {
      if (!this.events[event]) return;
      this.events[event] = this.events[event].filter((cb) => cb !== callback);
    },
    emit(event, data) {
      if (!this.events[event]) return;
      this.events[event].forEach((callback) => callback(data));
    },
  };

  // Loading state for marketplace changes
  const isChangingMarketplace = ref(false);
  // Single owner of isChangingMarketplace via a depth counter. Nested switches
  // (changeBusinessProfile → loadBusinessProfile → setCurrentMarketplace) keep the flag true
  // until the OUTERMOST one finishes, so consumers (dashboard.vue) reload exactly once on the
  // true→false transition. Replaces the previous fixed 100ms setTimeout — a crude "URL settled"
  // guess — and the two independent true/false writers (A#3).
  let marketplaceSwitchDepth = 0;
  const beginMarketplaceSwitch = () => {
    marketplaceSwitchDepth += 1;
    isChangingMarketplace.value = true;
  };
  const endMarketplaceSwitch = () => {
    marketplaceSwitchDepth = Math.max(0, marketplaceSwitchDepth - 1);
    if (marketplaceSwitchDepth === 0) isChangingMarketplace.value = false;
  };

  const syncedConfiguration = computed(() => currentMarketplace.value?.syncedConfiguration);

  // Active-marketplace account-type state — the global "which type is selected
  // right now" source. Classification logic lives in marketplaceAccountType.js;
  // these just bind it reactively to currentMarketplace so any component/guard
  // can branch on type without re-deriving it.
  const currentAccountType = computed(() => normalizeAccountType(currentMarketplace.value));
  const isSellerMarketplace = computed(() => isSellerProfile(currentMarketplace.value));
  const isVendorMarketplace = computed(() => isVendorProfile(currentMarketplace.value));
  const isBookMarketplace = computed(() => isBookProfile(currentMarketplace.value));
  const isSpEligibleMarketplace = computed(() => isSpEligibleProfile(currentMarketplace.value));

  // Semantic category of the active marketplace + capability gate. The single
  // reactive source every surface (nav, router guard, page banner, dropdown)
  // asks "can the active marketplace use feature X?" — see config/marketplaceCapabilities.js.
  const currentMarketplaceCategory = computed(() => marketplaceCategory(currentMarketplace.value));
  const canAccessFeature = (featureKey) => profileCanAccessFeature(currentMarketplace.value, featureKey);

  // Amazon's per-program `accessLevels` on the active marketplace (WP-804). Orthogonal to
  // account type: type decides what's visible, this decides what's editable. Only the
  // `campaign` program matters here — `billing: view` is common on fully-usable profiles.
  const canEditCurrentMarketplace = computed(() => hasEditAccess(currentMarketplace.value));

  // Throws before an Amazon write on a view-only marketplace — the shared last-line guard
  // for every store that mutates Amazon entities (analytics.js writes, aiActions.js approve).
  const assertCanEditMarketplace = () => {
    if (canEditCurrentMarketplace.value) return;
    throw new Error('Editing is not permitted — you have view-only access to this marketplace');
  };

  // A seller-only marketplace is selected: it came from a Selling Partner connection and
  // has no ads profile, so it isn't queryable yet (OLAP/ads requests are scoped by the ads
  // profileId via X-Profile-Id). Lets components tell "seller-only selected" apart from
  // "nothing selected" — both report withoutMarketplace=true ("no queryable marketplace").
  const isCurrentMarketplaceSellerOnly = computed(
    () => !!currentMarketplace.value && !currentMarketplace.value.profileId,
  );

  // Indicates that account data has been loaded (withoutBusinessProfile and withoutMarketplace are not null)
  const isDataLoaded = computed(
    () => withoutBusinessProfile.value !== null && withoutMarketplace.value !== null,
  );

  const getAccessTokenForProfile = (businessProfileId) => authStore.getAppToken(businessProfileId);

  const clearAccount = () => {
    businessProfiles.value = []; // keep the array invariant — other resets use [] too
    currentBusinessProfile.value = null;
    currentMarketplace.value = null;
    withoutBusinessProfile.value = null;
    withoutMarketplace.value = null;
    isLoadingProfiles.value = false;
    profileSyncStatus.value = 'idle';
    stopSyncPolling();
    autoDefaultCache.clear();
    confirmedDefaultMarketplaceUids.clear();
    unadoptedBusinessProfile.value = null;

    // Clear storage to prevent session leakage
    removeItemFromLocalStorage('currentBusinessProfile');
    removeItemFromLocalStorage('currentMarketplace');
    sessionStorage.removeItem('currentBusinessProfile');
    sessionStorage.removeItem('currentMarketplace');
  };

  const getRequestHeaders = (businessProfileId) => {
    const profileId = businessProfileId || currentBusinessProfile.value?.id;
    const token = profileId ? getAccessTokenForProfile(profileId) : null;
    return {
      Authorization: `Bearer ${token || ''}`,
      ...(profileId && { businessProfileId: profileId }),
    };
  };

  const fetchBusinessProfile = async () => {
    const res = await $axiosManager.get('/business-profiles');
    return res?.data;
  };

  const fetchAllBusinessProfiles = async () => {
    const res = await $axiosManager.get('/business-profiles/all');
    return res?.data ?? [];
  };

  const updateCurrentProfileAndMarketplace = (profiles, options = {}) => {
    const { updateUrl = false, persistToStorage = true } = options;

    if (!currentBusinessProfile.value?.id) {
      return;
    }

    const updatedProfile = profiles?.find((p) => p.id === currentBusinessProfile.value.id);
    if (!updatedProfile) {
      return;
    }

    // A profile is present — keep the flag in sync with reality. Without this the
    // flag could stay null/true and the analytics page would render the
    // "Create business profile" empty state for a user who already has one.
    withoutBusinessProfile.value = false;

    // Update profile
    if (updateUrl) {
      setCurrentBusinessProfile(updatedProfile, { updateUrl: true });
    } else {
      currentBusinessProfile.value = updatedProfile;
      if (persistToStorage) {
        setItemToSessionStorage('currentBusinessProfile', updatedProfile);
        setItemToLocalStorage('currentBusinessProfile', updatedProfile);
      }
    }

    // Update marketplace
    const current = currentMarketplace.value;
    const currentMpId = current?.profileId ?? null;
    const currentUid = getMarketplaceUid(current);

    if (current && currentMpId == null) {
      // A seller-only marketplace (no ads profile) is selected. Preserve it across this
      // background refresh — re-resolve the same entry (by uid) from the refreshed profile's
      // seller-only set; fall back to an ads default (or null) only if it disappeared.
      // Apply LOCALLY (like the ads branch) so multi-tab reconciliation doesn't fight over
      // the server-side default.
      const sellerOnly = buildSellerOnlyMarketplaces(updatedProfile);
      const resolvedMarketplace =
        sellerOnly.find((m) => getMarketplaceUid(m) === currentUid) ||
        findMarketplaceInProfile(updatedProfile);

      if (updateUrl) {
        setCurrentMarketplace(resolvedMarketplace, {
          updateUrl: true,
          persist: !isFallbackSellerPick(resolvedMarketplace, currentUid),
        });
      } else {
        currentMarketplace.value = resolvedMarketplace;
        withoutMarketplace.value = !resolvedMarketplace?.profileId;
        if (persistToStorage) {
          setItemToSessionStorage('currentMarketplace', resolvedMarketplace);
          setItemToLocalStorage('currentMarketplace', resolvedMarketplace);
        }
      }
    } else if (currentMpId == null) {
      // Onboarding: no marketplace selected yet, but the profile may now expose one
      // (e.g. right after connecting an Amazon Ads account). Route through
      // setCurrentMarketplace so withoutMarketplace flips to a concrete boolean and
      // consumers are notified. This cross-tab path stays synchronous and does not
      // rank or save a server default — a heuristic pick must not become the default.
      const resolvedMarketplace = findMarketplaceInProfile(updatedProfile);
      setCurrentMarketplace(resolvedMarketplace, {
        updateUrl,
        persist: false,
      });
    } else {
      // A marketplace was already selected. Either it still belongs to this profile
      // (refresh its object in place) OR it was removed elsewhere — e.g. the Amazon Ads
      // account was unlinked in another tab (WP-602 §4 #2) — in which case re-resolve to
      // a valid marketplace (or null). Apply the result LOCALLY: this runs in a background
      // multi-tab reconciliation, so it must NOT go through setCurrentMarketplace, which
      // would overwrite the user's server-side default marketplace (saveDefaultMarketplaceSetting)
      // and have tabs fight over it. We bypass the same-profileId guard so a refreshed
      // object (e.g. an updated syncedConfiguration flag) is still swapped in.
      const stillExists = extractMarketplacesFromProfile(updatedProfile).some(
        (p) => p.profileId === currentMpId,
      );
      const resolvedMarketplace = stillExists
        ? findMarketplaceInProfile(updatedProfile, currentMpId)
        : findMarketplaceInProfile(updatedProfile);

      if (updateUrl) {
        setCurrentMarketplace(resolvedMarketplace, { updateUrl: true });
      } else {
        currentMarketplace.value = resolvedMarketplace;
        withoutMarketplace.value = !resolvedMarketplace?.profileId;
        if (persistToStorage) {
          setItemToSessionStorage('currentMarketplace', resolvedMarketplace);
          setItemToLocalStorage('currentMarketplace', resolvedMarketplace);
        }
      }
    }
  };

  const extractMarketplacesFromProfile = (profile) => {
    return (
      profile?.amazonAdsAccounts
        ?.flatMap((account) => account.amazonAdsProfiles || [])
        .filter((p) => p.profileId) || []
    );
  };

  const findMarketplaceInProfile = (profile, preferredProfileId = null) => {
    const allMarketplaces = extractMarketplacesFromProfile(profile);

    // Try to find by preferred profileId first
    if (preferredProfileId) {
      const found = allMarketplaces.find((p) => p.profileId === preferredProfileId);
      if (found) return found;
    }

    // Fallback: the first selectable leaf of the dropdown (Ads first, then seller-only). The raw
    // ads heuristic below only remains for profiles whose Ads profiles are all non-selectable
    // (attribution / agency) with no seller-only marketplace, so those users keep their old pick.
    return (
      findDefaultMarketplace(profile) ||
      allMarketplaces.find((p) => p.sellerName?.trim()) ||
      allMarketplaces.find((p) => p.syncedConfiguration) ||
      allMarketplaces[0] ||
      null
    );
  };

  // Resolve a desired marketplace uid to a concrete entry on this profile. Prefers the union
  // resolver (handles ads + seller-only, returns the same selectable leaf the dropdown shows).
  // For a legacy ads uid that points to a profile the tree filters out (e.g. attribution/agency),
  // falls back to the raw ads-by-profileId match so we don't regress the old restore. Finally
  // falls back to the default-marketplace heuristic.
  const resolveMarketplaceForProfile = (profile, desiredUid) => {
    const byUid = findMarketplaceByUid(profile, desiredUid);
    if (byUid) return byUid;
    if (desiredUid?.startsWith('ads:')) {
      const pid = +desiredUid.slice(4);
      const raw = findMarketplaceInProfile(profile, pid);
      if (raw?.profileId === pid) return raw;
    }
    return findMarketplaceInProfile(profile);
  };

  // A seller-only marketplace the store picked by itself (nothing asked for it), as opposed to
  // one the user chose or one already saved as their default. Such a pick must not be saved as the
  // server default, or connecting Ads later would leave the user on the seller marketplace.
  const isFallbackSellerPick = (marketplace, desiredUid) =>
    !!marketplace && !marketplace.profileId && getMarketplaceUid(marketplace) !== desiredUid;

  const findBusinessProfileById = (profiles, id) => {
    return profiles?.find((bp) => bp.id === id) || null;
  };

  // Read the raw selection intent from the URL. These are an INTENT only — they are validated
  // against the owned businessProfiles set before being applied (see reconcileUrlToSelection).
  const readUrlIds = () => {
    try {
      const p = new URLSearchParams(window.location.search);
      const bpId = p.get('businessProfileId');
      const profileId = p.get('profileId');
      // `marketplace=<uid>` is the canonical marketplace intent (ads + seller-only); legacy
      // `profileId` is kept as an ads-only fallback so old links/bookmarks still resolve.
      const marketplaceUid = p.get('marketplace');
      return {
        bpId: bpId != null ? +bpId : null,
        profileId: profileId != null ? +profileId : null,
        marketplaceUid: marketplaceUid || null,
      };
    } catch {
      return { bpId: null, profileId: null, marketplaceUid: null };
    }
  };

  // Single priority resolver for the desired selection. Priority: URL intent → session/local
  // storage → server favorite → first owned profile. Ownership is NOT enforced here — that is
  // done where the owned set is known (syncBusinessProfiles / reconcileUrlToSelection).
  const resolveDesiredSelection = () => {
    const url = readUrlIds();
    const storedBp =
      getItemFromSessionStorage('currentBusinessProfile') ||
      getItemFromLocalStorage('currentBusinessProfile');
    const storedMp =
      getItemFromSessionStorage('currentMarketplace') ||
      getItemFromLocalStorage('currentMarketplace');
    const favorite = businessProfiles.value?.find((p) => p.isFavorite);
    const first = businessProfiles.value?.[0];
    // Selection intent (set from the backend's login default, or seeded from URL/storage at
    // bootstrap) outranks storage/favorite so the user lands on the backend-chosen profile.
    const intentBpId = bpSelection.get() != null ? +bpSelection.get() : null;
    return {
      bpId: url.bpId ?? intentBpId ?? storedBp?.id ?? favorite?.id ?? first?.id ?? null,
      profileId: url.profileId ?? storedMp?.profileId ?? null,
    };
  };

  const isOwnedProfile = (bpId) => !!businessProfiles.value?.some((bp) => bp.id === bpId);

  // True when `uid` is still a marketplace on this profile. Same check as
  // resolveMarketplaceForProfile's uid / raw-ads match, without the default fallback.
  const marketplaceExists = (profile, uid) => {
    if (!profile || !uid) return false;
    if (findMarketplaceByUid(profile, uid)) return true;
    if (typeof uid === 'string' && uid.startsWith('ads:')) {
      const pid = +uid.slice(4);
      if (!Number.isFinite(pid)) return false;
      return extractMarketplacesFromProfile(profile).some((p) => p.profileId === pid);
    }
    return false;
  };

  const isAbortError = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED';

  // Lazy: analytics.js imports this store, and amazonAdsApi is only needed to rank.
  const loadAutoDefaultDeps = async () => {
    const [{ useAnalytic }, { listCampaigns }] = await Promise.all([
      import('@/stores/analytics.js'),
      import('@/services/amazonAdsApi.js'),
    ]);
    const analytic = useAnalytic();
    return {
      getOverview: (payload, signal, config) =>
        analytic.getMarketplaceOverview(payload, signal, config),
      listCampaigns,
    };
  };

  // One 4s budget for both tiers. Abort stops the overview request; the race stops a
  // campaign count that the ads client cannot cancel. A timeout or a thrown error is not
  // cached, so a later load can try again.
  const settleAutoDefault = async (profile) => {
    const controller = new AbortController();
    let timedOut = false;
    let timer;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        resolve(null);
      }, AUTO_DEFAULT_BUDGET_MS);
    });
    const timeoutError = () => {
      const error = new Error('marketplace auto-default timed out');
      error.timedOut = true;
      return error;
    };
    try {
      const loaded = await Promise.race([loadAutoDefaultDeps(), timeout]);
      if (timedOut) throw timeoutError();
      const result = await Promise.race([
        pickAutoDefaultMarketplace(profile, { ...loaded, signal: controller.signal }),
        timeout,
      ]);
      if (timedOut) throw timeoutError();
      return result;
    } catch (error) {
      if (timedOut || isAbortError(error)) throw timeoutError();
      console.warn('[account] marketplace auto-default failed:', error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };

  const rankAutoDefault = (profile) => {
    if (!profile || selectableAdsCandidates(profile).length < 2) return null;
    const key = profile.id;
    if (key == null) return settleAutoDefault(profile).catch(() => null);
    const cached = autoDefaultCache.get(key);
    if (cached) return cached;

    // Only a real answer stays cached: a pick, or a miss where both tiers answered. A timeout,
    // a thrown error, or a miss that hid an error (`failed`) is dropped so the next resolution
    // ranks again.
    let promise;
    promise = settleAutoDefault(profile).then(
      (result) => {
        if (result?.failed && autoDefaultCache.get(key) === promise) autoDefaultCache.delete(key);
        return result;
      },
      () => {
        if (autoDefaultCache.get(key) === promise) autoDefaultCache.delete(key);
        return null;
      },
    );
    autoDefaultCache.set(key, promise);
    return promise;
  };

  // Which marketplace this profile should open on. Same order for the initial sync, a
  // dropdown switch, and a route-driven load (the cross-tab refresh does not rank):
  //   1) URL intent (`marketplace` uid, else legacy profileId), if it still exists here
  //   2) saved server default, if it still exists here
  //   3) spend, else enabled-campaign count
  //   4) session/local storage, if it still exists here
  //   5) { uid: null, source: 'fallback' } — the caller uses findMarketplaceInProfile
  // `source` is 'url' | 'saved' | 'spend' | 'campaign' | 'storage' | 'fallback'.
  const resolveDesiredMarketplace = async (
    profile,
    { preferredUid = null, preferredProfileId = null } = {},
  ) => {
    const urlUids = [];
    if (preferredUid) urlUids.push(preferredUid);
    if (preferredProfileId != null && preferredProfileId !== '') {
      urlUids.push(`ads:${preferredProfileId}`);
    }
    for (const uid of urlUids) {
      if (marketplaceExists(profile, uid)) return { uid, source: 'url' };
    }

    if (profile?.id) {
      const { marketplaceUid } = await getDefaultMarketplaceSetting(profile.id);
      if (marketplaceUid && marketplaceExists(profile, marketplaceUid)) {
        return { uid: marketplaceUid, source: 'saved' };
      }
    }

    const ranked = await rankAutoDefault(profile);
    const rankedUid = getMarketplaceUid(ranked?.marketplace);
    if (rankedUid && marketplaceExists(profile, rankedUid)) {
      return { uid: rankedUid, source: ranked.source };
    }

    const storedMp =
      getItemFromSessionStorage('currentMarketplace') ||
      getItemFromLocalStorage('currentMarketplace');
    const storedUid = getMarketplaceUid(storedMp);
    if (storedUid && marketplaceExists(profile, storedUid)) {
      return { uid: storedUid, source: 'storage' };
    }

    return { uid: null, source: 'fallback' };
  };

  // The ranking can hold a resolution for up to its 4s budget, during which the user may switch
  // business. Each resolution takes a number; only the newest may commit, so an older one never
  // writes its pick (or the saved default) under a business selected after it started.
  let marketplaceResolveGeneration = 0;
  const resolveLatestMarketplace = async (profile, preferred) => {
    const generation = ++marketplaceResolveGeneration;
    const resolved = await resolveDesiredMarketplace(profile, preferred);
    return generation === marketplaceResolveGeneration ? resolved : null;
  };

  // One setCurrentMarketplace per resolution. URL, saved, and spend picks are written
  // back unless they are a seller marketplace the store invented. Campaign, storage, and
  // fallback picks stay local.
  const commitResolvedMarketplace = (profile, resolved, { updateUrl }) => {
    const uid = resolved?.uid || null;
    const marketplace = uid
      ? resolveMarketplaceForProfile(profile, uid)
      : findMarketplaceInProfile(profile);
    const source = resolved?.source;
    const persist =
      (source === 'url' || source === 'saved' || source === 'spend') &&
      !isFallbackSellerPick(marketplace, uid);
    setCurrentMarketplace(marketplace, { updateUrl, persist });
  };

  // After the active selection is decided against the owned set, reconcile the URL to it. A URL
  // bpId/profileId that doesn't match the validated active selection was foreign or stale (a
  // shared link / old bookmark for a profile this user can't access) — strip it so the request
  // resolver can't keep minting tokens for it, and tell the user when a foreign profile was asked.
  const reconcileUrlToSelection = async ({ notify = true } = {}) => {
    // Skip if the route isn't resolved yet — a router.replace with a null name can bounce to '/'.
    // The guard's defense-in-depth strip repairs the URL on the next navigation as a backstop.
    if (!route.name) return;
    const url = readUrlIds();
    const activeBp = currentBusinessProfile.value?.id ?? null;
    const activeUid = getMarketplaceUid(currentMarketplace.value);
    const activeProfileId = currentMarketplace.value?.profileId ?? null;
    const query = { ...route.query };
    let changed = false;
    let foreignBp = false;

    if (url.bpId != null && url.bpId !== activeBp) {
      foreignBp = true;
      if (activeBp != null) query.businessProfileId = activeBp;
      else delete query.businessProfileId;
      changed = true;
    }
    // Marketplace: `marketplace=<uid>` is canonical; legacy `profileId` is treated as ads:<id>.
    // A URL marketplace that doesn't match the validated active selection was foreign/stale —
    // repair it (and keep the legacy profileId mirror in sync for ads, drop it for seller-only).
    const urlUid = url.marketplaceUid ?? (url.profileId != null ? `ads:${url.profileId}` : null);
    if (urlUid != null && urlUid !== activeUid) {
      if (activeUid != null) query.marketplace = activeUid;
      else delete query.marketplace;
      if (activeProfileId != null) query.profileId = activeProfileId;
      else delete query.profileId;
      changed = true;
    } else if (
      urlUid == null &&
      activeUid != null &&
      activeProfileId == null &&
      mirrorsMarketplaceInUrl(route.path || currentPath())
    ) {
      // A seller-only selection resolved without any URL intent (auto-selected fallback): write it
      // so the URL carries it like a manual pick would. Ads selections are left as before.
      query.marketplace = activeUid;
      changed = true;
    }

    if (!changed) return;
    // Await so the URL replace fully settles before sync returns — otherwise a
    // forward router.push issued by the caller right after syncBusinessProfiles()
    // races this in-flight replace and one of the two navigations is aborted
    // (first click "sticks", second click works). .catch swallows the benign
    // redirected/aborted rejection Vue Router raises on navigation overlap.
    await router.replace({ name: route.name, params: route.params, query }).catch(() => {});
    if (notify && foreignBp) {
      ElNotification({
        title: 'Profile not available',
        message: 'That business profile isn\'t available to you — opened an available one instead.',
        type: 'warning',
      });
    }
  };

  const setCurrentBusinessProfile = (profile, { updateUrl = true } = {}) => {
    currentBusinessProfile.value = profile;

    // Dual-write: sessionStorage для ізоляції табів + localStorage для fallback
    setItemToSessionStorage('currentBusinessProfile', profile);
    setItemToLocalStorage('currentBusinessProfile', profile);
    withoutBusinessProfile.value = profile === null;

    if (updateUrl) {
      const query = { ...route.query };
      if (profile?.id) {
        query.businessProfileId = profile.id;
      } else {
        delete query.businessProfileId;
      }
      router.replace({
        name: route.name,
        params: route.params,
        query,
      });
    }
  };

  // persist: false keeps the selection out of the server-side default (an auto-picked fallback).
  const setCurrentMarketplace = (marketplace, { updateUrl = true, persist = true } = {}) => {
    const previousMarketplace = currentMarketplace.value;

    // Identity is the stable marketplace uid (not profileId): seller-only marketplaces have
    // no profileId, so two distinct seller-only marketplaces would otherwise both compare
    // as `undefined === undefined` and a real switch between them would be dropped here.
    // Always refresh withoutMarketplace (needed for the initial null→null on an empty profile).
    if (getMarketplaceUid(previousMarketplace) === getMarketplaceUid(marketplace)) {
      withoutMarketplace.value = !marketplace?.profileId;
      return;
    }

    beginMarketplaceSwitch();
    currentMarketplace.value = marketplace;

    // Dual-write: sessionStorage для ізоляції табів + localStorage для fallback
    setItemToSessionStorage('currentMarketplace', marketplace);
    setItemToLocalStorage('currentMarketplace', marketplace);

    // Persist as default marketplace for current business profile on server. Stored by
    // marketplaceUid, so both ads and seller-only marketplaces persist (the uid round-trips
    // through the opaque user-setting value; see saveDefaultMarketplaceSetting).
    const bpId = currentBusinessProfile.value?.id;
    if (persist && bpId && getMarketplaceUid(marketplace)) {
      saveDefaultMarketplaceSetting(bpId, marketplace).catch(() => {});
    }

    // "Without marketplace" tracks the absence of a QUERYABLE (ads) marketplace: a
    // seller-only selection counts as withoutMarketplace=true so every ads-scoped page
    // (dashboard, legacy analytics, dayparting…) keeps its existing empty state and never
    // fires a query with no X-Profile-Id. OlapPage surfaces the seller-only info-state.
    withoutMarketplace.value = !marketplace?.profileId;

    // Повідомити про зміну маркетплейсу
    marketplaceEmitter.emit('marketplace-changed', {
      previous: previousMarketplace,
      current: marketplace,
    });

    if (updateUrl) {
      const query = { ...route.query };
      // Canonical marketplace intent: `marketplace=<uid>` (works for ads + seller-only).
      const uid = getMarketplaceUid(marketplace);
      // A Shopify analytics page is scoped by the store link, not an Amazon marketplace, so it
      // carries neither (the selection itself lives in the store and storage, not the URL).
      const mirror = mirrorsMarketplaceInUrl(route.path || currentPath());
      if (uid && mirror) {
        query.marketplace = uid;
      } else {
        delete query.marketplace;
      }
      // Legacy ads mirror — keep `profileId` for ads so old open tabs / bookmarks still resolve;
      // seller-only marketplaces have none, so it's dropped.
      if (marketplace?.profileId && mirror) {
        query.profileId = marketplace.profileId;
      } else {
        delete query.profileId;
      }
      // End the switch only AFTER the URL replace actually settles, so consumers that reload on
      // the true→false transition (dashboard.vue) see a stable URL. Deterministic replacement for
      // the old fixed 100ms timeout. .catch swallows the benign redirected/aborted navigation
      // failure Vue Router raises on overlap.
      router
        .replace({ name: route.name, params: route.params, query })
        .catch(() => {})
        .finally(endMarketplaceSwitch);
    } else {
      endMarketplaceSwitch();
    }
  };

  const loadBusinessProfile = async (id, { preferredProfileId = null, preferredUid = null } = {}) => {
    const requestKey = `profile-${id}`;
    if (pendingRequests.has(requestKey)) {
      return pendingRequests.get(requestKey);
    }

    const fetchPromise = (async () => {
      try {
        const profile = await fetchBusinessProfile();

        const resolved = await resolveLatestMarketplace(profile, {
          preferredUid,
          preferredProfileId,
        });
        // A newer resolution (another switch) owns the selection now.
        if (!resolved) return profile;

        setCurrentBusinessProfile(profile, { updateUrl: true });
        commitResolvedMarketplace(profile, resolved, { updateUrl: true });

        return profile;
      } catch (error) {
        setErrors(error);
        throw error;
      } finally {
        pendingRequests.delete(requestKey);
      }
    })();

    pendingRequests.set(requestKey, fetchPromise);
    return fetchPromise;
  };

  const changeBusinessProfile = async (profile, { navigateHome = true } = {}) => {
    if (!profile?.id) {
      throw new Error('Business profile must have an id');
    }

    const previousProfile = currentBusinessProfile.value;

    try {
      beginMarketplaceSwitch();

      // Set the BP intent EAGERLY — before any async — so requests fired during the switch
      // window (token mint below, marketplace load) already target the new BP. The store object
      // updates later via loadBusinessProfile and the sync watcher keeps bpSelection consistent;
      // a rollback cannot rely on that watcher (the previous id never changed, so it never
      // fires), which is why the catch below restores bpSelection explicitly.
      bpSelection.set(profile.id);

      // `navigateHome: false` (a caller that navigates itself right after) keeps its route but
      // still drops the previous profile's marketplace intent from the current URL.
      if (!navigateHome) {
        const query = { ...route.query, businessProfileId: profile.id };
        delete query.marketplace;
        delete query.profileId;
        await router.replace({ name: route.name, params: route.params, query });
      }

      // Explicit switch: always re-issue the scoped token via /auth/switch-profile (bypassing the
      // cache) so the backend rotates the token and persists this profile as the user's last-used.
      await authStore.switchToProfile(profile.id);

      // Load full profile data and set marketplace
      const loaded = await loadBusinessProfile(profile.id);

      // A switch always lands on the main dashboard with a clean URL: nothing of the previous
      // profile (campaign or entity ids in the path, filters, dates, `to`, OAuth return params)
      // may survive. Navigate only AFTER the new profile is loaded: the router guard's billing
      // gate reads currentBusinessProfile.plan, which must be the NEW profile's plan (navigating
      // earlier made a healthy profile inherit the previous one's gate and land on /billing).
      // The new profile's own marketplace rides along, as setCurrentMarketplace stamps it.
      if (navigateHome) {
        const query = { businessProfileId: profile.id };
        const marketplaceUid = getMarketplaceUid(currentMarketplace.value);
        const adsProfileId = currentMarketplace.value?.profileId;
        if (marketplaceUid) query.marketplace = marketplaceUid;
        if (adsProfileId) query.profileId = adsProfileId;
        // A guard redirect or superseded navigation is not a failed switch: never roll back for it.
        await router.replace({ ...getHomeRoute(), query }).catch(() => {});
      }

      return loaded;
    } catch (error) {
      console.error('[account] Failed to change business profile:', error);

      // Відновити попередній профіль
      if (previousProfile) {
        currentBusinessProfile.value = previousProfile;
        setItemToLocalStorage('currentBusinessProfile', previousProfile);
        // The id did not change, so the sync watcher stays silent: point the BP intent (read by
        // tokenService.resolveBusinessProfileId) back at the previous profile ourselves, or later
        // requests keep going out for the profile this switch was abandoned for.
        bpSelection.set(previousProfile.id);
        // If the URL already names the abandoned profile, point it back at the one still selected.
        const urlBpId = route.query?.businessProfileId;
        if (urlBpId != null && String(urlBpId) !== String(previousProfile.id)) {
          await router
            .replace({
              name: route.name,
              params: route.params,
              query: { ...route.query, businessProfileId: previousProfile.id },
            })
            .catch(() => {});
        }
      }

      // Показати помилку користувачу
      setErrors([
        {
          message: error.message || 'An error occurred while switching profiles',
          type: 'error',
        },
      ]);

      throw error;
    } finally {
      endMarketplaceSwitch();
    }
  };

  const ensureCurrentProfileLoaded = async () => {
    // If not logged in, don't attempt to load profiles
    if (!authStore.loggedIn) return null;

    // URL params are the source of truth - check them first
    const queryParams = new URLSearchParams(window.location.search);
    const queryBusinessProfileId = queryParams.get('businessProfileId');
    const queryProfileId = queryParams.get('profileId');
    const queryMarketplaceUid = queryParams.get('marketplace') || null;

    // Determine target IDs: URL params take priority. The marketplace intent (uid + legacy
    // profileId) stays URL-only — saved default, ranking, and storage are handled (in that
    // order) by resolveDesiredMarketplace, so we pass these as the preferred (URL intent) only.
    let targetBusinessProfileId = queryBusinessProfileId ? +queryBusinessProfileId : null;
    const targetProfileId = queryProfileId ? +queryProfileId : null;

    // Only use storage as fallback if URL params are not present
    if (!targetBusinessProfileId) {
      const savedProfile = getItemFromSessionStorage('currentBusinessProfile') || getItemFromLocalStorage('currentBusinessProfile');
      targetBusinessProfileId = savedProfile?.id;
    }

    if (!targetBusinessProfileId) {
      // No profile ID available, return current value
      return currentBusinessProfile.value;
    }

    // 1. If we are currently loading all profiles, wait for it first
    if (pendingRequests.has('all')) {
      await pendingRequests.get('all');
    }

    // 2. Check if current profile is already correct and matches target ID
    if (currentBusinessProfile.value?.id === targetBusinessProfileId) {
      return currentBusinessProfile.value;
    }

    // 3. Check if it exists in the already loaded list
    const profileFromList = businessProfiles.value?.find((p) => p.id === targetBusinessProfileId);
    if (profileFromList) {
      const resolved = await resolveLatestMarketplace(profileFromList, {
        preferredUid: queryMarketplaceUid,
        preferredProfileId: targetProfileId,
      });
      if (!resolved) return profileFromList;

      setCurrentBusinessProfile(profileFromList, { updateUrl: false });
      commitResolvedMarketplace(profileFromList, resolved, { updateUrl: false });

      return profileFromList;
    }

    // Ownership guard: if the owned set is loaded and the requested id isn't in it, the URL was
    // foreign/stale (shared link / old bookmark). Don't load it — keep the validated current
    // selection and repair the URL, so we never mint a token for a profile this user can't access.
    if (businessProfiles.value?.length && !isOwnedProfile(targetBusinessProfileId)) {
      await reconcileUrlToSelection();
      return currentBusinessProfile.value;
    }

    // 4. If still not found or we want fresh data, load it (this also handles deduplication internally)
    return await loadBusinessProfile(targetBusinessProfileId, {
      preferredProfileId: targetProfileId,
      preferredUid: queryMarketplaceUid,
    });
  };

  const createBusinessProfile = async (params) => {
    // A previous attempt already created the profile but could not adopt it: re-submitting the
    // form must retry the adoption, never POST a second business.
    if (unadoptedBusinessProfile.value) return retryBusinessProfileAdoption();

    let created;
    try {
      const res = await $axiosManager.post('/business-profiles', params);
      created = res?.data;
    } catch (error) {
      setErrors(error);
      throw error;
    }

    // Billing registers this profile's customer before its first status read (no 404). Marked
    // before the profile becomes current, because that is what starts the billing init.
    markNewBusinessProfile(created?.id);

    return finishNewBusinessProfile(created);
  };

  // Retry after createBusinessProfile reported that the session could not adopt the profile it
  // just created. The profile already exists server-side, so this never creates another one: it
  // re-runs the adoption (bypassing the claim failure latch) and the rest of the setup.
  const retryBusinessProfileAdoption = async () => {
    if (!unadoptedBusinessProfile.value) return null;
    return finishNewBusinessProfile(unadoptedBusinessProfile.value, { force: true });
  };

  // Adopts a just-created profile into the session, makes it current and syncs. Shared by
  // createBusinessProfile and retryBusinessProfileAdoption.
  const finishNewBusinessProfile = async (created, { force = false } = {}) => {
    // A pre-profile session (registration onboarding) must adopt its first profile into the
    // SESSION token BEFORE the profile becomes current: setCurrentBusinessProfile /
    // syncBusinessProfiles below trigger token resolution for it, and with a pre-profile claim
    // tokenResolver would mint a wp.auth.bp.<default> duplicate of the session's refresh chain
    // (backend reuse-detection → forced logout, WP-981). No-op for an already-scoped session.
    // A failure (after the claim's own refresh-and-retry) is surfaced, not swallowed: carrying on
    // pre-profile leaves the user with no access and no message. The profile is remembered so the
    // caller can offer a retry; the session is NOT logged out.
    if (created?.id != null) {
      try {
        await authStore.claimDefaultProfile(created.id, { force });
      } catch (error) {
        console.error('[createBusinessProfile] session re-scope to new default profile failed:', error);
        unadoptedBusinessProfile.value = created;
        const failure = new Error(
          i18n.global.t("We couldn't open your new business. Please try again."),
        );
        failure.code = 'BUSINESS_PROFILE_ADOPTION_FAILED';
        failure.cause = error;
        throw failure;
      }
    }
    unadoptedBusinessProfile.value = null;

    // Route through setCurrentBusinessProfile so withoutBusinessProfile flips to
    // false immediately (a raw assignment left it null/stale, which later made
    // the analytics empty-state show "Create business profile" for a user who
    // already had one).
    setCurrentBusinessProfile(created, { updateUrl: false });

    try {
      await syncBusinessProfiles();
    } catch (error) {
      console.error('[createBusinessProfile] syncBusinessProfiles failed:', error);
    }

    try {
      const attribution = consumeSignupAttribution();
      trackEvent('create_business_profile', {
        business_profile_id: currentBusinessProfile.value?.id,
        source: 'app',
      });
      const firstTouch = {};
      Object.entries({
        first_source: attribution?.utmSource,
        first_medium: attribution?.utmMedium,
        first_campaign: attribution?.utmCampaign,
        first_landing: attribution?.landingPage,
      }).forEach(([key, value]) => {
        if (typeof value === 'string' && value.trim()) firstTouch[key] = value;
      });
      // Raw dataLayer push (NOT trackEvent) is required: `bp_created` is consumed by a GTM
      // Custom Event trigger (customEvent == bp_created) → GA4 tag → Google Ads primary
      // conversion. That trigger only matches a dataLayer entry carrying an `event` key.
      // trackEvent() routes through window.gtag('event', …) (vue-gtag-next sets window.gtag),
      // which pushes an arguments array with no `event` key, so the GTM trigger never fires.
      // This exact line was accidentally dropped in 08f2b445 (WP-767), silently killing the
      // Ads conversion signal from ~2026-07-02 onward.
      window.dataLayer?.push({ event: 'bp_created', ...firstTouch });
    } catch (error) {
      console.error('[createBusinessProfile] tracking failed:', error);
    }

    return currentBusinessProfile.value;
  };





  const getBusinessProfileUsers = async () => {
    try {
      const res = await $axiosManager.get('/business-profiles/users');
      return res?.data;
    } catch (error) {
      setErrors(error);
      throw new Error(`Error: ${error}`);
    }
  };

  // Fire-and-forget cancel during page unload. `fetch` with `keepalive: true`
  // is required because beacons can't carry custom Authorization headers and
  // awaited axios calls don't survive unload.
  const cancelAmazonAdsIntegrationBeacon = (accountId, options = null) => {
    if (!accountId) return false;
    const bpId = options ? adsClaimBusinessId(options.businessProfileId) : currentBusinessProfile.value?.id;
    if (!bpId) return false;
    const token = getAccessTokenForProfile(bpId);
    if (!token) return false;
    const baseUrl = (getV2BaseUrl() || '').replace(/\/$/, '');
    const url = `${baseUrl}/amazon/ads/integrate/${accountId}/cancel`;
    try {
      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          businessProfileId: String(bpId),
          'Content-Type': 'application/json',
        },
        body: '{}',
      }).catch(() => {});
      return true;
    } catch (e) {
      return false;
    }
  };

  // Hard-deletes a freshly authorized Amazon Ads account when the user
  // dismisses the marketplace-selection step. Idempotent: 404 is treated as
  // success because the goal — "no orphan account" — is already achieved.
  // See issue #556 for the contract.
  const cancelAmazonAdsIntegration = async (accountId, options = null) => {
    if (!accountId) return { success: true, accountId: null, skipped: true };
    try {
      const bpId = options ? adsClaimBusinessId(options.businessProfileId) : currentBusinessProfile.value?.id;
      if (!bpId) throw new Error('No business profile ID available');

      const res = await $axios.post(
        `/amazon/ads/integrate/${accountId}/cancel`,
        {},
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(bpId),
        },
      );
      return res?.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return { success: true, accountId, alreadyGone: true };
      }
      setErrors(error);
      return Promise.reject(error);
    }
  };


  // V2 API base URL from environment variable
  const getV2BaseUrl = () => import.meta.env.VITE_API_BASE_URL_V2;

  // V2: Get all integrated accounts for current business profile
  const getIntegratedAccounts = async () => {
    try {
      const accountId = currentBusinessProfile.value?.id;
      if (!accountId) {
        console.warn('[account.js] getIntegratedAccounts: No business profile ID');
        return [];
      }

      const res = await $axios.get(
        '/amazon/ads/integrate',
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(accountId),
        },
      );

      return res?.data || [];
    } catch (error) {
      setErrors(error);
      // Return empty array on error to avoid breaking UI
      return [];
    }
  };

  // V2: Get all connections grouped by advertising account
  // Returns: { advertisingAccounts: [...], oauthConnections: [...] }
  // Failures resolve to empty lists (the other callers show nothing); `throwOnError` rethrows
  // them for a caller that must tell "failed" from "empty"; `silent` skips the error toast.
  const getBusinessProfileConnections = async ({ throwOnError = false, silent = false } = {}) => {
    try {
      const accountId = currentBusinessProfile.value?.id;
      if (!accountId) {
        return { advertisingAccounts: [], oauthConnections: [] };
      }

      const res = await $axios.get(
        '/amazon/ads/integrate/connections',
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(accountId),
        },
      );

      return res?.data || { advertisingAccounts: [], oauthConnections: [] };
    } catch (error) {
      if (!silent) setErrors(error);
      if (throwOnError) throw error;
      return { advertisingAccounts: [], oauthConnections: [] };
    }
  };

  // V2: Get full profile hierarchy for profile management dialog
  const getAccountProfilesForDialog = async (accountId) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) {
        console.warn('[account.js] getAccountProfilesForDialog: No business profile ID');
        return null;
      }

      const res = await $axios.get(
        `/amazon/ads/integrate/${accountId}/profiles`,
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(bpId),
        },
      );

      // Filter out unsupported profile types (AGENCY) from response
      const data = res?.data;
      if (data?.adsAccounts) {
        data.adsAccounts = data.adsAccounts
          .map((account) => ({
            ...account,
            profiles: (account.profiles || []).filter((p) => normalizeAccountType(p) !== 'AGENCY'),
          }))
          .filter((account) => account.profiles.length > 0);
      }

      return data;
    } catch (error) {
      console.error('[account.js] getAccountProfilesForDialog error:', error);
      setErrors(error);
      return null;
    }
  };

  const oauthStart = async (params) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) {
        throw new Error('No business profile ID available');
      }

      const res = await $axios.post('/amazon/ads/oauth/start',
        params,
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(bpId),
        },
      );
      return res?.data;
    } catch (error) {
      setErrors(error);
      return Promise.reject(error);
    }
  };


  const adsClaimRequest = async (path, body, { businessProfileId } = {}) => {
    const bpId = adsClaimBusinessId(businessProfileId);
    if (!bpId) throw Object.assign(new Error('No business profile ID available'), { code: 'ERR_NO_BUSINESS_PROFILE' });
    const res = await $axios.post(path, body, {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
    return res?.data;
  };
  const adsOauthPreview = (claimToken, options) =>
    adsClaimRequest('/amazon/ads/oauth/claim/preview', { claimToken }, options);
  const adsOauthClaim = (claimToken, options) =>
    adsClaimRequest('/amazon/ads/oauth/claim', { claimToken }, options);
  const adsOauthCancel = (claimToken, options) =>
    adsClaimRequest('/amazon/ads/oauth/claim/cancel', { claimToken }, options);
  const oauthGetSession = (sessionId, options) =>
    adsClaimRequest('/amazon/ads/oauth/session/result', { sessionId }, options);

  const updateProfileConnections = async (accountId, connect = [], disconnect = [], options = null) => {
    try {
      const bpId = options ? adsClaimBusinessId(options.businessProfileId) : currentBusinessProfile.value?.id;
      if (!bpId) {
        throw new Error('No business profile ID available');
      }

      const body = { connect, disconnect };
      const res = await $axios.patch(
        `/amazon/ads/integrate/${accountId}/profiles`,
        body,
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(bpId),
        },
      );
      return res?.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const archiveAccount = async (accountId) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) {
        throw new Error('No business profile ID available');
      }

      const res = await $axios.post(`/amazon/ads/integrate/${accountId}/archive`,
        {},
        {
          baseURL: getV2BaseUrl(),
          headers: getRequestHeaders(bpId),
        },
      );
      return res?.data;
    } catch (error) {
      console.error('[account.js] archiveAccount error:', error);
      return Promise.reject(error);
    }
  };

  // The Python service owns the OAuth round-trip; tokens never reach the browser.
  // Its JSON is snake_case, so we map to the camelCase the components expect.

  const mapSpMarketplace = (m) => ({
    marketplaceId: m.marketplace_id,
    countryCode: m.country_code,
    name: m.name,
    defaultCurrencyCode: m.default_currency_code,
    defaultLanguageCode: m.default_language_code,
    domainName: m.domain_name,
    isParticipating: m.is_participating,
    hasSuspendedListings: m.has_suspended_listings,
    isConnected: m.is_connected ?? false,
    marketplaceType: m.marketplace_type ?? 'RETAIL',
    storeName: m.store_name ?? null,
  });

  const mapSpAccount = (a) => ({
    id: a.id,
    sellingPartnerId: a.selling_partner_id,
    alias: a.alias ?? '',
    region: a.region,
    isActive: a.is_active,
    accountType: a.account_type,
    // Authorizing Amazon identity — surfaced on the "no marketplaces" recovery state
    // (#871). Backend #880 will populate these for GET /amazon/sp/integrate; until then
    // they are empty and the UI falls back to a generic label.
    userName: a.user_name ?? '',
    userEmail: a.user_email ?? '',
    marketplaces: (a.marketplaces || []).map(mapSpMarketplace),
  });

  // One seller-authorization row (mirrors the ads OAuth-connection shape).
  const mapSpAuthorization = (a) => ({
    id: a.id,
    sellingPartnerId: a.selling_partner_id,
    accountType: a.account_type,
    status: a.status,
    marketplacesCount: a.marketplaces_count,
    connectedMarketplacesCount: a.connected_marketplaces_count,
    lastRefreshedAt: a.last_refreshed_at,
    createdAt: a.created_at,
    userId: a.user_id,
    userName: a.user_name,
    userEmail: a.user_email,
    creatorRole: a.creator_role,
  });

  // Start the backend-managed SP OAuth flow; returns the Amazon consent URL.
  // redirectUrl tells the backend where to send the user back after consent.
  // Pass accountId to reconnect an existing account: the backend derives
  // account_type/region/marketplace_ids from the stored account and ignores
  // the other fields.
  const spOauthStart = async ({
    accountType = 'SELLER',
    region = null,
    marketplaceIds = null,
    redirectUrl = null,
    accountId = null,
    confirmed = false,
  } = {}) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) throw new Error('No business profile ID available');
      const res = await $axios.post(
        '/amazon/sp/integrate/start',
        {
          account_type: accountType,
          region,
          marketplace_ids: marketplaceIds,
          redirect_url: redirectUrl,
          account_id: accountId,
          confirmed,
        },
        { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
      );
      // `warnings` is advisory — authorize_url is always present alongside it. When the
      // array is non-empty the caller confirms with the user and calls again with
      // confirmed: true, which is the call that records OAUTH_STARTED. Redirect using
      // the URL from that second call, not this one.
      return {
        authorizeUrl: res?.data?.authorize_url,
        state: res?.data?.state,
        warnings: res?.data?.warnings || [],
      };
    } catch (error) {
      setErrors(error);
      return Promise.reject(error);
    }
  };

  // Friendlier copy for the claim-specific failures; other codes keep the backend's message.
  const spClaimErrorMessages = {
    CANCELLED:
      'This Amazon connection attempt was cancelled. Please connect the seller account again.',
    CLAIM_EXPIRED:
      'The Amazon authorization expired before it could be saved. Please connect the seller account again.',
    CLAIM_FORBIDDEN:
      'This Amazon authorization was opened by a different WisePPC user and was discarded for security. Please connect the seller account again.',
  };

  // Preview and confirm use only the callback's fixed BP. The single-use token stays
  // in the request body. The review component handles errors without logging token-bearing
  // request configs; an uncertain outcome is checked with preview before another confirm.
  const spClaimRequest = async (path, spSession, { businessProfileId } = {}) => {
    const bpId = spClaimBusinessId(businessProfileId);
    if (!bpId) {
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post(
      path,
      { sp_session: spSession },
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    return res?.data || {};
  };

  const mapSpClaimResult = (data) => ({
    status: data.status,
    accountId: data.account_id ?? null,
    reconnected: !!data.reconnected,
    errorCode: data.error_code ?? null,
    error: spClaimErrorMessages[data.error_code] ?? data.error ?? null,
  });

  // All claim operations require the callback's saved BP, never the global selection.
  const spOauthClaim = async (spSession, options) =>
    mapSpClaimResult(await spClaimRequest('/amazon/sp/integrate/claim', spSession, options));

  const spOauthCancel = async (spSession, options) =>
    mapSpClaimResult(await spClaimRequest('/amazon/sp/integrate/claim/cancel', spSession, options));

  const spOauthPreview = async (spSession, options) => {
    const data = await spClaimRequest('/amazon/sp/integrate/claim/preview', spSession, options);
    return {
      ...mapSpClaimResult(data),
      businessProfileId: data.business_profile_id,
      sellingPartnerId: data.selling_partner_id,
      accountType: data.account_type,
      region: data.region,
      marketplaces: data.marketplaces?.map((marketplace) => ({
        marketplaceId: marketplace.marketplace_id,
        countryCode: marketplace.country_code,
        name: marketplace.name,
        storeName: marketplace.store_name,
      })),
      expiresAt: data.expires_at,
      isReconnect: !!data.is_reconnect,
    };
  };

  // FE-facing connections shape that powers the seller-accounts + authorizations
  // tables. Returns { accounts, authorizations }; each account carries the
  // authNumber (#N) of the authorization it belongs to (the "Authorizations"
  // column), derived from the authorizations order.
  // `silent` skips the global error toast (the rejection still propagates) for callers that
  // render their own failure state, like the Overview's "Needs attention" strip.
  const fetchSpConnections = async ({ silent = false } = {}) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) return { accounts: [], authorizations: [] };
      const res = await $axios.get('/amazon/sp/integrate/connections', {
        baseURL: getV2BaseUrl(),
        headers: getRequestHeaders(bpId),
      });
      const data = res?.data || {};
      const authorizations = (data.sp_authorizations || []).map(mapSpAuthorization);
      const authNumberBySpId = new Map(authorizations.map((a, i) => [a.sellingPartnerId, i + 1]));
      const accounts = (data.amazon_sp_accounts || []).map((a) => ({
        sellingPartnerId: a.selling_partner_id,
        alias: a.alias ?? '',
        authNumber: authNumberBySpId.get(a.selling_partner_id) ?? null,
        marketplaces: (a.marketplaces || []).map(mapSpMarketplace),
      }));
      return { accounts, authorizations };
    } catch (error) {
      if (!silent) setErrors(error);
      return Promise.reject(error);
    }
  };

  // Full account list (incl. id + per-marketplace isConnected) for the finish step.
  const fetchSpAccounts = async ({ businessProfileId } = {}) => {
    try {
      const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
      if (!bpId) return [];
      const res = await $axios.get('/amazon/sp/integrate', {
        baseURL: getV2BaseUrl(),
        headers: getRequestHeaders(bpId),
      });
      return (res?.data || []).map(mapSpAccount);
    } catch (error) {
      setErrors(error);
      return Promise.reject(error);
    }
  };

  // Archive (soft-delete) a seller authorization. Backend rejects with 409 if any
  // marketplace is still connected.
  const spArchiveAuthorization = async (accountId) => {
    try {
      const bpId = currentBusinessProfile.value?.id;
      if (!bpId) throw new Error('No business profile ID available');
      await $axios.post(
        `/amazon/sp/integrate/${accountId}/archive`,
        {},
        { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
      );
    } catch (error) {
      setErrors(error);
      return Promise.reject(error);
    }
  };

  /**
   * Hard-delete a seller account (rollback of a wrong connection).
   * Used by the connect-flow Cancel: the just-linked account and its marketplaces
   * are removed outright. Not blocked by connected marketplaces (unlike archive).
   */
  const spDeleteAccount = async (accountId, { businessProfileId } = {}) => {
    try {
      const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
      if (!bpId) throw new Error('No business profile ID available');
      await $axios.delete(`/amazon/sp/integrate/${accountId}`, {
        baseURL: getV2BaseUrl(),
        headers: getRequestHeaders(bpId),
      });
    } catch (error) {
      setErrors(error);
      return Promise.reject(error);
    }
  };

  // eBay seller connect. The integration service speaks camelCase here, so bodies and
  // answers pass through unmapped. No setErrors in any eBay action: the eBay page maps each
  // failure to its own message (or an inline error state), and setErrors would add a second,
  // raw "Request failed with status code …" toast, since these errors nest under `detail`.

  // Start the eBay consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (any status, ACTIVE included).
  const ebayOauthStart = async ({ redirectUrl, accountId = null } = {}) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    const res = await $axios.post(
      '/ebay/oauth/start',
      { redirectUrl, ...(accountId != null && { accountId }) },
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // A failed claim answers 200 with only an errorCode, so the message is chosen here.
  const ebayClaimErrorMessages = {
    CANCELLED:
      'This eBay connection attempt was cancelled. Please connect the eBay account again.',
    CLAIM_EXPIRED:
      'The eBay authorization expired before it could be saved. Please connect the eBay account again.',
    CLAIM_FORBIDDEN:
      'This eBay authorization was opened by a different WisePPC user and was discarded for security. Please connect the eBay account again.',
    RECONNECT_TARGET_INVALID:
      'The eBay connection you were reconnecting was removed in the meantime. Please connect the eBay account again.',
    MARKETPLACE_CONFLICT:
      'An eBay account registered on this marketplace is already connected to this business profile. Delete that connection first, or connect this eBay account in a different business profile.',
  };

  // Claim the grant the eBay callback parked. The callback redirects with ?ebayClaim=<token>,
  // a single-use secret only the consenting browser receives; the account is written only
  // when the user who started the flow claims it here. The token goes in the body to keep it
  // out of access logs. A 'failed' claim is a normal 200 answer; 404 (unknown / another
  // user's / another BP's token), 403 (role) and 500 (transient, grant kept) reject — the
  // page decides which of those keep the token for a retry.
  const ebayClaimRequest = async (path, claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (!Number.isSafeInteger(bpId) || bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) || !/^\d+$/.test(String(businessProfileId))) {
      // Local, not an HTTP failure: a retry cannot fix it, so the page treats this code as
      // definitive rather than as a retryable network error.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post(
      path,
      { claimToken },
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    const data = res?.data || {};
    const errorCode = data.errorCode ?? null;
    let error = null;
    if (!['success', 'ready', 'cancelled', 'already_completed'].includes(data.status)) {
      error =
        ebayClaimErrorMessages[errorCode] ??
        `Could not complete the eBay connection${errorCode ? ` (${errorCode})` : ''}. Please connect the eBay account again.`;
    }
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      errorCode,
      error,
    };
  };

  // All claim operations require the callback's saved BP, never the mutable global selection.
  const ebayOauthClaim = (claimToken, options) =>
    ebayClaimRequest('/ebay/oauth/claim', claimToken, options);
  const ebayOauthPreview = (claimToken, options) =>
    ebayClaimRequest('/ebay/oauth/claim/preview', claimToken, options);
  const ebayOauthCancel = (claimToken, options) =>
    ebayClaimRequest('/ebay/oauth/claim/cancel', claimToken, options);

  // Every eBay account of the current business profile, any status, oldest first.
  const fetchEbayConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/ebay/connections', {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
      timeout: 5000,
    });
    return res?.data || [];
  };

  // Clears the stored token (best-effort revoke at eBay) → DISCONNECTED; returns the updated
  // connection. Idempotent.
  const ebayDisconnect = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    const res = await $axios.post(
      `/ebay/connections/${connectionId}/disconnect`,
      {},
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    return res?.data;
  };

  const ebayDeleteConnection = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    await $axios.delete(`/ebay/connections/${connectionId}`, {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
  };

  // Shopify store links. Same conventions as eBay (camelCase passthrough, no setErrors: the
  // Shopify pages map every failure themselves). The install starts in Shopify admin, never
  // here: App Home mints a single-use claim token and opens
  // /integrations/shopify/claim#shopifyClaim=<token>; these actions preview and claim it.

  // Codes the claim flow can answer with (preview / claim HTTP errors and 'failed' claims).
  const shopifyClaimErrorMessages = {
    CLAIM_NOT_FOUND:
      'This Shopify link is not valid, or it was already used by another WisePPC user or business profile. Open WisePPC from your Shopify admin (Apps → WisePPC) to get a new link.',
    CLAIM_EXPIRED:
      'This Shopify link expired. Open WisePPC from your Shopify admin (Apps → WisePPC) to get a new link.',
    CLAIM_ALREADY_USED:
      'This Shopify link was already used. Open WisePPC from your Shopify admin (Apps → WisePPC) to get a new link.',
    SHOP_NOT_ACTIVE:
      'The WisePPC app is no longer installed on this Shopify store. Reinstall or open it from your Shopify admin (Apps → WisePPC) to get a new link.',
    FORBIDDEN_ROLE:
      'Only an owner or admin of the selected business profile can link a Shopify store. Ask one to link it, or cancel and start a new link for another business profile.',
  };

  // The message for a claim-flow errorCode, with a generic fallback naming the code.
  const shopifyClaimErrorMessage = (errorCode) =>
    shopifyClaimErrorMessages[errorCode] ??
    `Could not link the Shopify store${errorCode ? ` (${errorCode})` : ''}. Open WisePPC from your Shopify admin (Apps → WisePPC) to get a new link.`;

  // Names the shop behind a claim token without consuming it: { shopName, shopDomain, expiresAt }.
  // JWT only — no businessProfileId header, the profile is chosen after the preview (any
  // profile's app token proves who the user is). Rejects on 404 / 409 / 410 / 500.
  const shopifyClaimPreview = async (claimToken) => {
    const bpId = currentBusinessProfile.value?.id;
    const token = bpId ? getAccessTokenForProfile(bpId) : null;
    const res = await $axios.post(
      '/shopify/claims/preview',
      { claimToken },
      {
        baseURL: getV2BaseUrl(),
        headers: { Authorization: `Bearer ${token || ''}` },
        skipBusinessProfileId: true,
      },
    );
    const data = res?.data || {};
    return {
      shopName: data.shopName ?? null,
      shopDomain: data.shopDomain ?? null,
      expiresAt: data.expiresAt ?? null,
    };
  };

  // Links the shop to the explicitly confirmed business profile (admin/owner). The token goes in the body
  // to keep it out of access logs. Every business outcome is a 200: 'success' (new, relinked,
  // or LINK_EXISTS_ACTIVE — already linked, idempotent) or 'failed' with an errorCode.
  // 403 FORBIDDEN_ROLE (checked before the token is touched, so it stays claimable), 404 and
  // 500 reject — the page retries the same user/BP after an ambiguous outcome.
  const shopifyClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = businessProfileId;
    if (!Number.isSafeInteger(bpId) || bpId <= 0) {
      // Local, not an HTTP failure: a retry cannot fix it (same code as the eBay claim).
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post(
      '/shopify/claims',
      { claimToken },
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    const data = res?.data || {};
    const errorCode = data.errorCode ?? null;
    return {
      status: data.status,
      linkId: data.linkId ?? null,
      isRelink: !!data.isRelink,
      alreadyLinked: data.status === 'success' && errorCode === 'LINK_EXISTS_ACTIVE',
      errorCode,
      error: data.status === 'success' ? null : shopifyClaimErrorMessage(errorCode),
    };
  };

  // Every Shopify link of a business profile (default: the current one), any status, oldest
  // first.
  const fetchShopifyConnections = async (businessProfileId) => {
    const bpId = businessProfileId || currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/shopify/connections', {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
    return res?.data || [];
  };

  // Link → DISCONNECTED (the app stays installed, the shop's token is untouched); returns the
  // updated connection. Idempotent. Relinking needs a new claim from Shopify admin.
  const shopifyDisconnect = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    const res = await $axios.post(
      `/shopify/connections/${connectionId}/disconnect`,
      {},
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    return res?.data;
  };

  // Deletes the link row; the shop stays installed.
  const shopifyDeleteConnection = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    await $axios.delete(`/shopify/connections/${connectionId}`, {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
  };

  // Walmart accounts. A seller key is registered by WisePPC support (an operator CLI), so
  // there is no connect action here: the page lists, disconnects and deletes. A 404 on the
  // list means Walmart is not available in this environment (prod).

  // Every Walmart account of the current business profile, any status, oldest first. The
  // response never carries a credential.
  const fetchWalmartConnections = async () => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/walmart/connections', {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
    return res?.data || [];
  };

  // Clears the stored credentials -> DISCONNECTED; returns the updated connection. Walmart has
  // no revoke call, so the key stays valid there until the seller regenerates it. Idempotent.
  const walmartDisconnect = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    const res = await $axios.post(
      `/walmart/connections/${connectionId}/disconnect`,
      {},
      { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) },
    );
    return res?.data;
  };

  const walmartDeleteConnection = async (connectionId) => {
    const bpId = currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    await $axios.delete(`/walmart/connections/${connectionId}`, {
      baseURL: getV2BaseUrl(),
      headers: getRequestHeaders(bpId),
    });
  };

  // Google connect (Ads first). Same conventions as eBay: the integration service speaks
  // camelCase, so bodies and answers pass through unmapped, and there is no setErrors: the Google
  // pages map every failure by its errorCode. Every call names its business profile.
  const googleRequest = (businessProfileId) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    return { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) };
  };

  // Start the Google consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (any status).
  const googleOauthStart = async ({ product = 'GOOGLE_ADS', redirectUrl, accountId = null } = {}) => {
    const res = await $axios.post(
      '/google/oauth/start',
      { product, redirectUrl, ...(accountId != null && { accountId }) },
      googleRequest(),
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // Claim the grant the Google callback parked (?googleClaim=<token>, a single-use secret). The
  // token goes in the body to keep it out of access logs. Idempotent: a repeat answers success
  // again. A failed claim is a normal 200 with errorCode; 404 / 403 / 5xx reject. The business
  // profile is the callback's, never the current selection.
  const googleOauthClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (!Number.isSafeInteger(bpId) || bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) || !/^\d+$/.test(String(businessProfileId))) {
      // Local, not an HTTP failure: a retry cannot fix it.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post('/google/oauth/claim', { claimToken }, googleRequest(bpId));
    const data = res?.data || {};
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      grantedProducts: data.grantedProducts || [],
      errorCode: data.errorCode ?? null,
    };
  };

  // Every non-archived Google connection of the business profile, oldest first.
  const fetchGoogleConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/google/connections', googleRequest(bpId));
    return res?.data || [];
  };

  const fetchGoogleConnection = async (connectionId, { businessProfileId } = {}) => {
    const res = await $axios.get(`/google/connections/${connectionId}`, googleRequest(businessProfileId));
    return res?.data;
  };

  // One page of the picker: { items, nextCursor }. params: resourceType, connected, q, limit, cursor.
  const fetchGoogleResources = async (connectionId, params = {}) => {
    const res = await $axios.get(`/google/connections/${connectionId}/resources`, {
      ...googleRequest(),
      params,
    });
    return { items: res?.data?.items || [], nextCursor: res?.data?.nextCursor ?? null };
  };

  // changes: [{ resourceType, resourceId, connect }], at most 500, applied all or nothing.
  // Answers { connected, disconnected, connection }.
  const changeGoogleResources = async (connectionId, changes) => {
    const res = await $axios.post(
      `/google/connections/${connectionId}/resources`,
      { changes },
      googleRequest(),
    );
    return res?.data;
  };

  // Re-run discovery now (1 per minute per connection; 429 carries Retry-After).
  const refreshGoogleResources = async (connectionId) => {
    const res = await $axios.post(
      `/google/connections/${connectionId}/resources/refresh`,
      {},
      googleRequest(),
    );
    return res?.data;
  };

  // Disconnects every resource -> INACTIVE; returns the updated connection. No Google call.
  // With `product` only that product's resources go off and the others keep their picks.
  const googleDisconnect = async (connectionId, { product = null } = {}) => {
    const res = await $axios.post(
      `/google/connections/${connectionId}/disconnect`,
      {},
      { ...googleRequest(), ...(product && { params: { product } }) },
    );
    return res?.data;
  };

  // "Remove": archives the connection; the last one using a Google login also revokes there.
  const googleArchive = async (connectionId) => {
    await $axios.post(`/google/connections/${connectionId}/archive`, {}, googleRequest());
  };

  // TikTok Ads connect. Same conventions as Google: the integration service speaks camelCase, so
  // bodies and answers pass through unmapped, and the pages map every failure by its errorCode.
  // Every call names its business profile. Scope ids arrive as strings (some exceed 2**53).
  const tiktokRequest = (businessProfileId) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    return { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) };
  };

  // Start the TikTok consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (any status). There is no product, app id or scope to send.
  const tiktokOauthStart = async ({ redirectUrl, accountId = null } = {}) => {
    const res = await $axios.post(
      '/tiktok/ads/oauth/start',
      { redirectUrl, ...(accountId != null && { accountId }) },
      tiktokRequest(),
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // Claim the grant the TikTok callback parked (?tiktokClaim=<token>, a single-use secret). The
  // token goes in the body to keep it out of access logs. Idempotent: a repeat answers the same
  // result. A failed claim is a normal 200 with status 'failed' and errorCode; 404 / 403 / 5xx
  // reject. The business profile is the callback's, never the current selection.
  const tiktokOauthClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (!Number.isSafeInteger(bpId) || bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) || !/^\d+$/.test(String(businessProfileId))) {
      // Local, not an HTTP failure: a retry cannot fix it.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post('/tiktok/ads/oauth/claim', { claimToken }, tiktokRequest(bpId));
    const data = res?.data || {};
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      discoveryStatus: data.discoveryStatus ?? null,
      gmvMaxScopeGranted: data.gmvMaxScopeGranted ?? null,
      errorCode: data.errorCode ?? null,
    };
  };

  // Every non-archived TikTok Ads connection of the business profile.
  const fetchTikTokConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/tiktok/ads/connections', tiktokRequest(bpId));
    return res?.data || [];
  };

  const fetchTikTokConnection = async (connectionId, { businessProfileId } = {}) => {
    const res = await $axios.get(
      `/tiktok/ads/connections/${connectionId}`,
      tiktokRequest(businessProfileId),
    );
    return res?.data;
  };

  // One page of the picker: { items, nextCursor }. params: connected, q, limit, cursor.
  const fetchTikTokResources = async (connectionId, params = {}) => {
    const res = await $axios.get(`/tiktok/ads/connections/${connectionId}/resources`, {
      ...tiktokRequest(),
      params,
    });
    return { items: res?.data?.items || [], nextCursor: res?.data?.nextCursor ?? null };
  };

  // changes: [{ advertiserId, connect }], at most 500, applied all or nothing.
  // Answers { connected, disconnected, connection }.
  const changeTikTokResources = async (connectionId, changes) => {
    const res = await $axios.post(
      `/tiktok/ads/connections/${connectionId}/resources`,
      { changes },
      tiktokRequest(),
    );
    return res?.data;
  };

  // Re-run discovery now (1 per minute per connection; 429 carries Retry-After).
  const refreshTikTokResources = async (connectionId) => {
    const res = await $axios.post(
      `/tiktok/ads/connections/${connectionId}/resources/refresh`,
      {},
      tiktokRequest(),
    );
    return res?.data;
  };

  // Disconnects every advertiser -> INACTIVE; returns the updated connection. No TikTok call.
  const tiktokDisconnect = async (connectionId) => {
    const res = await $axios.post(
      `/tiktok/ads/connections/${connectionId}/disconnect`,
      {},
      tiktokRequest(),
    );
    return res?.data;
  };

  // "Remove": archives the connection; the last one using a TikTok login also revokes there.
  const tiktokArchive = async (connectionId) => {
    await $axios.post(`/tiktok/ads/connections/${connectionId}/archive`, {}, tiktokRequest());
  };

  // Pinterest connect. Same conventions as Google and TikTok: the integration service speaks
  // camelCase, so bodies and answers pass through unmapped, and the pages map every failure by its
  // errorCode. Every call names its business profile. Ad account ids arrive as strings.
  const pinterestRequest = (businessProfileId) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    return { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) };
  };

  // Start the Pinterest consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (any status). There is no product, app id or scope to send.
  const pinterestOauthStart = async ({ redirectUrl, accountId = null } = {}) => {
    const res = await $axios.post(
      '/pinterest/oauth/start',
      { redirectUrl, ...(accountId != null && { accountId }) },
      pinterestRequest(),
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // Claim the grant the Pinterest callback parked (?pinterestClaim=<token>, a single-use secret).
  // The token goes in the body to keep it out of access logs. Idempotent: a repeat answers the same
  // result. A failed claim is a normal 200 with status 'failed' and errorCode; 404 / 403 / 5xx
  // reject (a 500 INTERNAL_ERROR is returned as a body, not raised). The business profile is the
  // callback's, never the current selection.
  const pinterestOauthClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (
      !Number.isSafeInteger(bpId) ||
      bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) ||
      !/^\d+$/.test(String(businessProfileId))
    ) {
      // Local, not an HTTP failure: a retry cannot fix it.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post('/pinterest/oauth/claim', { claimToken }, pinterestRequest(bpId));
    const data = res?.data || {};
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      discoveryStatus: data.discoveryStatus ?? null,
      errorCode: data.errorCode ?? null,
    };
  };

  // Every non-archived Pinterest connection of the business profile.
  const fetchPinterestConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/pinterest/connections', pinterestRequest(bpId));
    return res?.data || [];
  };

  const fetchPinterestConnection = async (connectionId, { businessProfileId } = {}) => {
    const res = await $axios.get(
      `/pinterest/connections/${connectionId}`,
      pinterestRequest(businessProfileId),
    );
    return res?.data;
  };

  // One page of the picker: { items, nextCursor }. params: resourceType, connected, q, limit, cursor.
  const fetchPinterestResources = async (connectionId, params = {}) => {
    const res = await $axios.get(`/pinterest/connections/${connectionId}/resources`, {
      ...pinterestRequest(),
      params,
    });
    return { items: res?.data?.items || [], nextCursor: res?.data?.nextCursor ?? null };
  };

  // changes: [{ resourceType: 'AD_ACCOUNT', resourceId, connect }], at most 500, applied all or
  // nothing. Answers { connected, disconnected, connection }.
  const changePinterestResources = async (connectionId, changes) => {
    const res = await $axios.post(
      `/pinterest/connections/${connectionId}/resources`,
      { changes },
      pinterestRequest(),
    );
    return res?.data;
  };

  // Re-run discovery now (1 per minute per connection; 429 carries Retry-After).
  const refreshPinterestResources = async (connectionId) => {
    const res = await $axios.post(
      `/pinterest/connections/${connectionId}/resources/refresh`,
      {},
      pinterestRequest(),
    );
    return res?.data;
  };

  // Disconnects every ad account -> INACTIVE; returns the updated connection. No Pinterest call.
  const pinterestDisconnect = async (connectionId) => {
    const res = await $axios.post(
      `/pinterest/connections/${connectionId}/disconnect`,
      {},
      pinterestRequest(),
    );
    return res?.data;
  };

  // "Remove": archives the connection and drops our copy of the tokens. No Pinterest call: the
  // user removes WisePPC in Pinterest (Settings -> Security) to end the grant there.
  const pinterestArchive = async (connectionId) => {
    await $axios.post(`/pinterest/connections/${connectionId}/archive`, {}, pinterestRequest());
  };

  // Meta Ads connect. Same conventions as Google: the integration service speaks camelCase, so
  // bodies and answers pass through unmapped, and the pages map every failure by its errorCode.
  // Every call names its business profile. Ad account ids arrive as bare strings (no act_ prefix).
  const metaRequest = (businessProfileId) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    return { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) };
  };

  // Start the Meta consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (any status). There is no scope, product or configuration to
  // send: one start is one Login for Business grant.
  const metaOauthStart = async ({ redirectUrl, accountId = null } = {}) => {
    const res = await $axios.post(
      '/meta/oauth/start',
      { redirectUrl, ...(accountId != null && { accountId }) },
      metaRequest(),
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // Claim the grant the Meta callback parked (?metaClaim=<token>, a single-use secret). The
  // token goes in the body to keep it out of access logs. Idempotent: a repeat answers the same
  // result. A failed claim is a normal 200 with status 'failed' and errorCode; 404 / 403 / 5xx
  // reject. The business profile is the callback's, never the current selection.
  const metaOauthClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (!Number.isSafeInteger(bpId) || bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) || !/^\d+$/.test(String(businessProfileId))) {
      // Local, not an HTTP failure: a retry cannot fix it.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post('/meta/oauth/claim', { claimToken }, metaRequest(bpId));
    const data = res?.data || {};
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      discoveryStatus: data.discoveryStatus ?? null,
      writeScopeGranted: data.writeScopeGranted ?? null,
      errorCode: data.errorCode ?? null,
    };
  };

  // Every non-archived Meta Ads connection of the business profile.
  const fetchMetaConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/meta/connections', metaRequest(bpId));
    return res?.data || [];
  };

  const fetchMetaConnection = async (connectionId, { businessProfileId } = {}) => {
    const res = await $axios.get(
      `/meta/connections/${connectionId}`,
      metaRequest(businessProfileId),
    );
    return res?.data;
  };

  // One page of the picker: { items, nextCursor }. params: connected, q, limit, cursor.
  const fetchMetaResources = async (connectionId, params = {}) => {
    const res = await $axios.get(`/meta/connections/${connectionId}/resources`, {
      ...metaRequest(),
      params,
    });
    return { items: res?.data?.items || [], nextCursor: res?.data?.nextCursor ?? null };
  };

  // changes: [{ adAccountId, connect }], at most 500, applied all or nothing.
  // Answers { connected, disconnected, connection }.
  const changeMetaResources = async (connectionId, changes) => {
    const res = await $axios.post(
      `/meta/connections/${connectionId}/resources`,
      { changes },
      metaRequest(),
    );
    return res?.data;
  };

  // Re-run discovery now (1 per minute per connection; 429 carries Retry-After).
  const refreshMetaResources = async (connectionId) => {
    const res = await $axios.post(
      `/meta/connections/${connectionId}/resources/refresh`,
      {},
      metaRequest(),
    );
    return res?.data;
  };

  // Disconnects every ad account -> INACTIVE; returns the updated connection. No Meta call.
  const metaDisconnect = async (connectionId) => {
    const res = await $axios.post(
      `/meta/connections/${connectionId}/disconnect`,
      {},
      metaRequest(),
    );
    return res?.data;
  };

  // "Remove": archives the connection (no Meta call: the user removes the app in Meta's Connected apps).
  const metaArchive = async (connectionId) => {
    await $axios.post(`/meta/connections/${connectionId}/archive`, {}, metaRequest());
  };

  // Microsoft Advertising (Bing) connect. Same conventions as TikTok: the integration service
  // speaks camelCase, so bodies and answers pass through unmapped, and the pages map every failure
  // by its errorCode. Every call names its business profile. In picker rows and change bodies
  // accountId is the Microsoft account id (a string) and customerId its customer; the connection
  // id in the path is our own row id.
  const bingRequest = (businessProfileId) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) throw new Error('No business profile ID available');
    return { baseURL: getV2BaseUrl(), headers: getRequestHeaders(bpId) };
  };

  // Start the Microsoft consent round-trip; the caller sends the browser to authorizeUrl.
  // accountId = reconnect target (our connection id, any status). Nothing else is sent.
  const bingOauthStart = async ({ redirectUrl, accountId = null } = {}) => {
    const res = await $axios.post(
      '/bing/oauth/start',
      { redirectUrl, ...(accountId != null && { accountId }) },
      bingRequest(),
    );
    return { authorizeUrl: res?.data?.authorizeUrl, sessionId: res?.data?.sessionId };
  };

  // Claim the grant the Bing callback parked (?bingClaim=<token>, a single-use secret). The
  // token goes in the body to keep it out of access logs. Idempotent: a repeat answers the same
  // result. A failed claim is a normal 200 with status 'failed' and errorCode; 404 / 403 / 5xx
  // reject. The business profile is the callback's, never the current selection.
  const bingOauthClaim = async (claimToken, { businessProfileId } = {}) => {
    const bpId = Number(businessProfileId);
    if (!Number.isSafeInteger(bpId) || bpId <= 0 ||
      !['number', 'string'].includes(typeof businessProfileId) || !/^\d+$/.test(String(businessProfileId))) {
      // Local, not an HTTP failure: a retry cannot fix it.
      const error = new Error('No business profile ID available');
      error.code = 'ERR_NO_BUSINESS_PROFILE';
      throw error;
    }
    const res = await $axios.post('/bing/oauth/claim', { claimToken }, bingRequest(bpId));
    const data = res?.data || {};
    return {
      ...data,
      accountId: data.accountId ?? null,
      isReconnect: !!data.isReconnect,
      discoveryStatus: data.discoveryStatus ?? null,
      errorCode: data.errorCode ?? null,
    };
  };

  // Every non-archived Bing connection of the business profile.
  const fetchBingConnections = async ({ businessProfileId } = {}) => {
    const bpId = businessProfileId ?? currentBusinessProfile.value?.id;
    if (!bpId) return [];
    const res = await $axios.get('/bing/connections', bingRequest(bpId));
    return res?.data || [];
  };

  const fetchBingConnection = async (connectionId, { businessProfileId } = {}) => {
    const res = await $axios.get(
      `/bing/connections/${connectionId}`,
      bingRequest(businessProfileId),
    );
    return res?.data;
  };

  // One page of the picker: { items, nextCursor }. params: connected, q, status, limit, cursor.
  const fetchBingResources = async (connectionId, params = {}) => {
    const res = await $axios.get(`/bing/connections/${connectionId}/resources`, {
      ...bingRequest(),
      params,
    });
    return { items: res?.data?.items || [], nextCursor: res?.data?.nextCursor ?? null };
  };

  // changes: [{ resourceType, customerId, accountId, connect }], at most 500, applied all or
  // nothing. Answers { connected, disconnected, connection }.
  const changeBingResources = async (connectionId, changes) => {
    const res = await $axios.post(
      `/bing/connections/${connectionId}/resources`,
      { changes },
      bingRequest(),
    );
    return res?.data;
  };

  // Re-run discovery now (1 per minute per connection; 429 carries Retry-After).
  const refreshBingResources = async (connectionId) => {
    const res = await $axios.post(
      `/bing/connections/${connectionId}/resources/refresh`,
      {},
      bingRequest(),
    );
    return res?.data;
  };

  // Disconnects every account -> INACTIVE; returns the updated connection. No Microsoft call.
  const bingDisconnect = async (connectionId) => {
    const res = await $axios.post(
      `/bing/connections/${connectionId}/disconnect`,
      {},
      bingRequest(),
    );
    return res?.data;
  };

  // "Remove": archives the connection. Microsoft has no revoke endpoint, so the user removes the
  // app's access at account.live.com/consent/Manage.
  const bingArchive = async (connectionId) => {
    await $axios.post(`/bing/connections/${connectionId}/archive`, {}, bingRequest());
  };

  /**
   * Update business profile data (e.g. name).
   * Backend identifies the profile via businessProfileId in the app JWT claim.
   */
  const updateBusinessProfileData = async (id, updates) => {
    try {
      const res = await $axiosManager.patch('/business-profiles', updates);
      const updated = res?.data;

      if (currentBusinessProfile.value?.id === id) {
        currentBusinessProfile.value = { ...currentBusinessProfile.value, ...updated };
        setItemToSessionStorage('currentBusinessProfile', currentBusinessProfile.value);
        setItemToLocalStorage('currentBusinessProfile', currentBusinessProfile.value);
      }

      if (businessProfiles.value) {
        const idx = businessProfiles.value.findIndex((p) => p.id === id);
        if (idx !== -1) {
          businessProfiles.value[idx] = { ...businessProfiles.value[idx], ...updated };
        }
      }

      return updated;
    } catch (error) {
      setErrors(error);
      throw error;
    }
  };

  /**
   * Set or clear the server-side favorite business profile.
   * @param {number|null} idOrNull — profile id to mark as favorite, or null to clear
   */
  const setFavoriteBusinessProfile = async (idOrNull) => {
    try {
      const res = await $axiosManager.put('/business-profiles/favorite', {
        businessProfileId: idOrNull,
      });

      if (businessProfiles.value) {
        businessProfiles.value = businessProfiles.value.map((p) => ({
          ...p,
          isFavorite: p.id === idOrNull,
        }));
      }

      if (currentBusinessProfile.value) {
        currentBusinessProfile.value = {
          ...currentBusinessProfile.value,
          isFavorite: currentBusinessProfile.value.id === idOrNull,
        };
        setItemToSessionStorage('currentBusinessProfile', currentBusinessProfile.value);
        setItemToLocalStorage('currentBusinessProfile', currentBusinessProfile.value);
      }

      return res?.data;
    } catch (error) {
      setErrors(error);
      throw error;
    }
  };

  // Derive { marketplaceUid, profileId } from whatever a caller passes: a marketplace object
  // (setCurrentMarketplace), a uid string, or a legacy numeric profileId (the ads-only settings
  // UI). One normalized shape persisted for both ads and seller-only marketplaces.
  const toDefaultMarketplaceValue = (mp) => {
    if (mp == null) return { marketplaceUid: null, profileId: null };
    if (typeof mp === 'object') {
      return { marketplaceUid: getMarketplaceUid(mp), profileId: mp.profileId ?? null };
    }
    // A uid string (ads:<id> / sp:<sid>:<mkt>).
    if (typeof mp === 'string' && (mp.startsWith('ads:') || mp.startsWith('sp:'))) {
      const pid = mp.startsWith('ads:') ? Number(mp.slice(4)) : NaN;
      return { marketplaceUid: mp, profileId: Number.isFinite(pid) ? pid : null };
    }
    // Legacy ads profileId (number or numeric string).
    const pid = Number(mp);
    return Number.isFinite(pid)
      ? { marketplaceUid: `ads:${pid}`, profileId: pid }
      : { marketplaceUid: null, profileId: null };
  };

  // Read a stored default-marketplace value of either shape into { marketplaceUid, profileId }.
  // New rows store { marketplaceUid, profileId }; legacy rows store only { profileId } → derive
  // the ads uid from it so old data keeps resolving.
  const normalizeStoredDefault = (value) => {
    const profileId = value?.profileId ?? null;
    const marketplaceUid = value?.marketplaceUid ?? (profileId ? `ads:${profileId}` : null);
    return { marketplaceUid, profileId };
  };

  /**
   * Get the saved default marketplace for a business profile as { marketplaceUid, profileId }.
   * `marketplaceUid` covers ads + seller-only; `profileId` is kept for ads-only consumers.
   * Uses user-settings API with localStorage fallback.
   */
  const getDefaultMarketplaceSetting = async (bpId) => {
    try {
      const res = await getUserSettingsByPage('global', { key: `default-marketplace-${bpId}` });
      const items = res?.data?.items || [];
      if (items.length > 0) {
        const value = normalizeStoredDefault(items[0].value);
        if (value.marketplaceUid) {
          confirmedDefaultMarketplaceUids.set(String(bpId), value.marketplaceUid);
        } else {
          confirmedDefaultMarketplaceUids.delete(String(bpId));
        }
        return value;
      }
    } catch (error) {
      console.warn('[account] getDefaultMarketplaceSetting server error, trying localStorage:', error);
    }

    // A missing row or failed read cannot confirm a localStorage fallback.
    confirmedDefaultMarketplaceUids.delete(String(bpId));
    // Fallback to mock service (localStorage)
    try {
      return normalizeStoredDefault(await businessProfileMockService.getDefaultMarketplace(bpId));
    } catch {
      return { marketplaceUid: null, profileId: null };
    }
  };

  /**
   * Save the default marketplace for a business profile. Accepts a marketplace object (ads or
   * seller-only), a uid string, or a legacy numeric profileId. Stores { marketplaceUid, profileId }
   * to the user-settings API + localStorage fallback. No-op when nothing resolves to a uid.
   */
  const saveDefaultMarketplaceSetting = async (bpId, marketplace) => {
    const value = toDefaultMarketplaceValue(marketplace);
    if (!value.marketplaceUid) return;

    // Save to localStorage fallback
    businessProfileMockService.setDefaultMarketplace(bpId, value);

    if (confirmedDefaultMarketplaceUids.get(String(bpId)) === value.marketplaceUid) return;

    try {
      await upsertUserSetting({
        page_name: 'global',
        key: `default-marketplace-${bpId}`,
        value,
        is_personal: true,
      });
      confirmedDefaultMarketplaceUids.set(String(bpId), value.marketplaceUid);
    } catch (error) {
      setErrors(error);
      throw error;
    }
  };

  // Helper: Check if profiles need sync. Only an Ads marketplace has a sync configuration to wait
  // for; a seller-only one (no profileId) never gets one, so polling for it would run forever.
  const needsProfileSync = () => {
    const marketplace = currentMarketplace.value;
    return !!marketplace?.profileId && !marketplace.syncedConfiguration;
  };

  // Helper: Start polling for unsynced marketplaces
  const startSyncPolling = () => {
    if (syncPollingInterval) return; // Already running

    syncPollingInterval = setInterval(async () => {
      if (needsProfileSync()) {
        await syncBusinessProfiles();
      } else {
        stopSyncPolling();
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Helper: Stop polling
  const stopSyncPolling = () => {
    if (syncPollingInterval) {
      clearInterval(syncPollingInterval);
      syncPollingInterval = null;
    }
  };

  // Active sync promise for deduplication — concurrent callers await the same result
  let activeSyncPromise = null;

  // Ensure isDataLoaded settles on every sync exit path — the OLAP grid mount gates rely
  // on it. Flags derive from the ACTUAL selection (not potential data), so a failure
  // between the BP and marketplace steps leaves withoutMarketplace=true, not a null hang.
  const settleSelectionFlags = () => {
    if (withoutBusinessProfile.value === null) {
      withoutBusinessProfile.value = !businessProfiles.value?.length;
    }
    if (withoutMarketplace.value === null) {
      withoutMarketplace.value = !currentMarketplace.value?.profileId;
    }
  };

  // Main sync method - SINGLE source for loading profiles
  const syncBusinessProfiles = async () => {
    // If already syncing, return the existing promise so callers await the same result
    if (activeSyncPromise) {
      return activeSyncPromise;
    }

    if (!authStore.isAuthenticated) {
      console.warn('syncBusinessProfiles: not authenticated, skipping sync.');
      return;
    }

    isLoadingProfiles.value = true;
    isSyncingFromThisTab = true;
    profileSyncStatus.value = 'syncing';

    activeSyncPromise = (async () => {
      try {
        let profiles;
        try {
          profiles = await fetchAllBusinessProfiles();
        } catch (err) {
          if (err?.response?.status === 400) {
            // Backend returns 400 when business profile context is not yet available
            // (e.g. mid-registration, missing OAuth2 token). Treat as empty state.
            console.warn('syncBusinessProfiles: /businessProfiles/all returned 400, skipping sync.');
            businessProfiles.value = [];
            settleSelectionFlags();
            return;
          }
          throw err;
        }
        businessProfiles.value = profiles;

        // Set current business profile if not set
        if (!currentBusinessProfile.value && profiles && profiles.length > 0) {
          // Single resolver (URL intent → storage → favorite → first). Validate the desired bpId
          // against the owned set: a foreign/stale URL id is dropped here and we fall back to
          // favorite/first, so we never select a profile this user can't access.
          const desired = resolveDesiredSelection();

          let profileToSet =
            (desired.bpId != null && profiles.find((p) => p.id === desired.bpId)) ||
            profiles.find((p) => p.isFavorite) ||
            profiles[0];

          // ALWAYS await CASL abilities BEFORE flipping UI state to true
          if (profileToSet?.id) {
            try {
              await authStore.getAppAccessToken(profileToSet.id);
            } catch (e) {
              console.error('[Profile Sync] Initial token fetch failed', e);
            }
          }

          // updateUrl: false — reconcileUrlToSelection below repairs the URL once, after the
          // marketplace is resolved (a router.replace mid-sync can catch a null route.name).
          setCurrentBusinessProfile(profileToSet, { updateUrl: false });

          // URL → saved default → spend/campaign rank → storage → dropdown fallback.
          // preferred* is the URL intent only, so a saved default outranks a stale
          // storage value — same ordering as loadBusinessProfile / dropdown switch.
          const desiredUrl = readUrlIds();
          const resolved = await resolveLatestMarketplace(profileToSet, {
            preferredUid: desiredUrl.marketplaceUid,
            preferredProfileId: desiredUrl.profileId,
          });
          // null: the user switched business while this was ranking; that switch commits.
          if (resolved) {
            commitResolvedMarketplace(profileToSet, resolved, { updateUrl: false });

            // Repair the URL to the validated active selection — strips a foreign/stale
            // bpId/profileId (shared link / old bookmark) and notifies. No-op when it already matched.
            await reconcileUrlToSelection();
          }
        } else if (currentBusinessProfile.value?.id) {
          try {
            await authStore.getAppAccessToken(currentBusinessProfile.value.id);
          } catch (e) {
            console.error('[Profile Sync] Existing token fetch failed', e);
          }

          updateCurrentProfileAndMarketplace(profiles, {
            updateUrl: false,
            persistToStorage: false,
          });
        } else if (!profiles || profiles.length === 0) {
          // Explicitly clear so `isDataLoaded` can resolve to true when user has zero profiles
          setCurrentBusinessProfile(null, { updateUrl: false });
          setCurrentMarketplace(null, { updateUrl: false });
          // Set minimum authenticated abilities — no business profile yet,
          // but the user IS logged in and should access User-level pages.
          authStore.setAuthenticatedAbilities();
        }

        lastProfileSync.value = Date.now();
        profileSyncStatus.value = 'synced';

        // Notify other tabs to refresh (send simple signal, not complex data)
        profileSyncChannel?.postMessage({
          type: 'profiles-updated',
          timestamp: Date.now(),
        });

        // Check if we need to start polling
        if (needsProfileSync()) {
          startSyncPolling();
        }

        return businessProfiles.value;
      } catch (error) {
        console.error('[Profile Sync] Failed to sync profiles:', error);
        profileSyncStatus.value = 'error';

        settleSelectionFlags();

        // If it's an authentication error (401), don't throw - let logout handle it
        if (error.response?.status === 401 || error.message?.includes('401')) {
          console.warn('[Profile Sync] Authentication error detected - ignore here, let interceptor handle it');

          // DO NOT clear local state to prevent stale data
          // Clearing here causes race conditions across tabs when tokens are refreshing

          return [];
        }

        throw error;
      } finally {
        isLoadingProfiles.value = false;
        activeSyncPromise = null;
        // Reset flag after a short delay to allow message to propagate
        setTimeout(() => {
          isSyncingFromThisTab = false;
        }, 100);
      }
    })();

    return activeSyncPromise;
  };

  // Cleanup function
  const cleanup = () => {
    stopSyncPolling();
    profileSyncChannel?.close();
  };

  // Watch for login state changes
  watch(() => authStore.loggedIn, async (isLoggedIn, wasLoggedIn) => {
    if (isLoggedIn && !wasLoggedIn) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('inviteCode')) {
        return;
      }

      const isOAuthRoute = router.currentRoute.value.path.includes('/oauth-landing');
      if (isOAuthRoute) {
        return;
      }

      const isRegistrationRoute = router.currentRoute.value.path.startsWith('/register');
      if (isRegistrationRoute) {
        return;
      }

      // login.vue owns the post-login sync and the preloader (redirectAfterLogin). Running here
      // too, this watcher's finally dropped the preloader as soon as the shared sync settled —
      // while login.vue was still navigating — so the login form flashed back up before the
      // dashboard rendered. A sign-in from another tab is synced by crossTabSync's onSignIn.
      if (router.currentRoute.value.name === 'login') {
        return;
      }

      try {
        loaderStore.showLoader();
        await syncBusinessProfiles();
      } catch (error) {
        console.error('[Profile Sync] Auto-load failed:', error);
        // Redundant safety net — syncBusinessProfiles already settles on its own exit paths.
        settleSelectionFlags();
      } finally {
        loaderStore.hideLoader();
      }
    }

    // Clear on logout
    if (!isLoggedIn && wasLoggedIn) {
      clearAccount();
    }
  }, { immediate: false });

  // Initial load handler - for page refresh when already logged in
  if (typeof window !== 'undefined') {
    router.isReady().then(() => {
      if (authStore.loggedIn && businessProfiles.value.length === 0) {
        const queryParams = new URLSearchParams(window.location.search);
        const inviteCode = queryParams.get('inviteCode');
        const isOAuthRoute = router.currentRoute.value.path.includes('/oauth-landing');

        if (!inviteCode && !isOAuthRoute) {
          loaderStore.showLoader();
          syncBusinessProfiles()
            .catch((error) => {
              console.error('[Profile Sync] Initial load failed:', error);
            })
            .finally(() => {
              loaderStore.hideLoader();
            });
        } else {
          loaderStore.hideLoader();
        }
      } else if (
        !authStore.loggedIn &&
        !router.currentRoute.value.path.includes('/oauth-landing')
      ) {
        // The session was dropped during the initial navigation (e.g. the guard signing out a
        // user who opened an invite link): App.vue only hides the bootstrap preloader when the
        // user was already signed out at mount, so without this it spins until a reload.
        loaderStore.hideLoader();
      }
    });
  }


  return {
    currentBusinessProfile,
    currentMarketplace,
    withoutBusinessProfile,
    withoutMarketplace,
    syncedConfiguration,
    currentAccountType,
    isSellerMarketplace,
    isVendorMarketplace,
    isBookMarketplace,
    isSpEligibleMarketplace,
    currentMarketplaceCategory,
    canAccessFeature,
    canEditCurrentMarketplace,
    assertCanEditMarketplace,
    isCurrentMarketplaceSellerOnly,
    isDataLoaded,
    // Event system
    marketplaceEmitter,
    isChangingMarketplace,
    // Pure fetch functions
    fetchBusinessProfile,
    fetchAllBusinessProfiles,
    // Helper functions
    extractMarketplacesFromProfile,
    findMarketplaceInProfile,
    findBusinessProfileById,
    // Pure set functions
    setCurrentBusinessProfile,
    setCurrentMarketplace,
    // Orchestrator functions
    loadBusinessProfile,
    changeBusinessProfile,
    ensureCurrentProfileLoaded,
    // Other functions
    createBusinessProfile,
    retryBusinessProfileAdoption,
    unadoptedBusinessProfile,
    businessProfiles,
    getBusinessProfileUsers,
    getIntegratedAccounts,
    getBusinessProfileConnections,
    getAccountProfilesForDialog,
    oauthStart,
    oauthGetSession,
    adsOauthPreview,
    adsOauthClaim,
    adsOauthCancel,
    updateProfileConnections,
    spOauthStart,
    spOauthClaim,
    spOauthCancel,
    spOauthPreview,
    fetchSpConnections,
    fetchSpAccounts,
    spArchiveAuthorization,
    spDeleteAccount,
    ebayOauthStart,
    ebayOauthClaim,
    ebayOauthPreview,
    ebayOauthCancel,
    fetchEbayConnections,
    ebayDisconnect,
    ebayDeleteConnection,
    shopifyClaimPreview,
    shopifyClaim,
    shopifyClaimErrorMessage,
    fetchShopifyConnections,
    shopifyDisconnect,
    shopifyDeleteConnection,
    fetchWalmartConnections,
    walmartDisconnect,
    walmartDeleteConnection,
    googleOauthStart,
    googleOauthClaim,
    fetchGoogleConnections,
    fetchGoogleConnection,
    fetchGoogleResources,
    changeGoogleResources,
    refreshGoogleResources,
    googleDisconnect,
    googleArchive,
    tiktokOauthStart,
    tiktokOauthClaim,
    fetchTikTokConnections,
    fetchTikTokConnection,
    fetchTikTokResources,
    changeTikTokResources,
    refreshTikTokResources,
    tiktokDisconnect,
    tiktokArchive,
    pinterestOauthStart,
    pinterestOauthClaim,
    fetchPinterestConnections,
    fetchPinterestConnection,
    fetchPinterestResources,
    changePinterestResources,
    refreshPinterestResources,
    pinterestDisconnect,
    pinterestArchive,
    metaOauthStart,
    metaOauthClaim,
    fetchMetaConnections,
    fetchMetaConnection,
    fetchMetaResources,
    changeMetaResources,
    refreshMetaResources,
    metaDisconnect,
    metaArchive,
    bingOauthStart,
    bingOauthClaim,
    fetchBingConnections,
    fetchBingConnection,
    fetchBingResources,
    changeBingResources,
    refreshBingResources,
    bingDisconnect,
    bingArchive,
    archiveAccount,
    cancelAmazonAdsIntegration,
    cancelAmazonAdsIntegrationBeacon,
    clearAccount,
    // Centralized sync system
    isLoadingProfiles,
    lastProfileSync,
    profileSyncStatus,
    syncBusinessProfiles,
    cleanup,
    // Business Profile Settings
    updateBusinessProfileData,
    setFavoriteBusinessProfile,
    getDefaultMarketplaceSetting,
    saveDefaultMarketplaceSetting,
  };
});
